import React from "react";
import {
  AMOUNT_UNITS,
  displayValue,
  formatAmount,
} from "../../mock/operationsJournalMock";
import { getSubconto } from "../../mock/sourceData";
import { AnalyticsCell, Checkbox, SortIcon } from "../tableParts";
import "../../OperationsJournal.css";

const renderCell = (value, options = {}) => {
  const text = displayValue(value);
  if (text === "—") {
    return <span className="oj-empty">—</span>;
  }
  if (options.link) {
    return (
      <button type="button" className="oj-link">
        {text}
      </button>
    );
  }
  return text;
};

const SortableHeader = ({ children, className }) => (
  <th className={className}>
    {children} <SortIcon />
  </th>
);

const DocumentCell = ({ row, onOpen }) => (
  <td>
    <span className="oj-doc">
      <button
        type="button"
        className="oj-link"
        onClick={
          onOpen
            ? (event) => {
                event.stopPropagation();
                onOpen(row);
              }
            : undefined
        }
      >
        {row.document}
      </button>
      {row.hasWarning && (
        <span
          className="oj-doc__warning"
          title={row.warningTitle || "Требует внимания"}
        />
      )}
    </span>
  </td>
);

/**
 * Таблица журнала операций в сжатом или расширенном виде (слои «Управленческие» и «Исходные»).
 * selection — необязательный выбор строк чекбоксами: { selectedIds: Set, onToggle(row), onTogglePage(rows, checked) }.
 * onOpenDocument — клик по номеру документа (если не задан, срабатывает клик по строке).
 */
function JournalTable({
  rows,
  pageStart,
  viewMode,
  visibleColumns,
  unit,
  isSource = false,
  selectedId,
  onRowClick,
  onOpenDocument,
  selection,
}) {
  const amountUnitLabel = AMOUNT_UNITS[unit].label;
  const pageSelectedCount = selection
    ? rows.filter((row) => selection.selectedIds.has(row.id)).length
    : 0;
  const isPageChecked = rows.length > 0 && pageSelectedCount === rows.length;

  const renderExpandedCell = (row, column) => {
    switch (column.type) {
      case "number":
        return (
          <td key={column.key} className="oj-cell--num">
            {row[column.key] == null ? (
              <span className="oj-empty">—</span>
            ) : (
              row[column.key]
            )}
          </td>
        );
      case "amount":
        return (
          <td key={column.key} className="oj-cell--num oj-cell--amount">
            {formatAmount(row[column.key], unit)}
          </td>
        );
      case "source":
        return (
          <td key={column.key}>
            <div className="oj-stack">
              <div>{row.source}</div>
              <div className="oj-stack__secondary">{row.dataType}</div>
            </div>
          </td>
        );
      default:
        return (
          <td key={column.key}>
            {renderCell(row[column.key], { link: column.type === "link" })}
          </td>
        );
    }
  };

  const selectionHeader = selection && (
    <th className="oj-cell--check">
      <Checkbox
        checked={isPageChecked}
        indeterminate={pageSelectedCount > 0 && !isPageChecked}
        label="Выбрать все операции на странице"
        onChange={() => selection.onTogglePage(rows, !isPageChecked)}
      />
    </th>
  );

  const renderLeadingCells = (row, index) => (
    <>
      {selection && (
        <td className="oj-cell--check">
          <Checkbox
            checked={selection.selectedIds.has(row.id)}
            label={`Выбрать операцию ${row.document}`}
            onChange={() => selection.onToggle(row)}
          />
        </td>
      )}
      <td className="oj-cell--index">{pageStart + index + 1}</td>
      <td>{row.date}</td>
      <DocumentCell row={row} onOpen={onOpenDocument} />
      <td>{row.organization}</td>
    </>
  );

  const leadingHeaders = (
    <>
      {selectionHeader}
      <th className="oj-cell--index">№</th>
      <SortableHeader>Дата</SortableHeader>
      <th>Документ</th>
      <SortableHeader>Организация</SortableHeader>
    </>
  );

  const getRowClassName = (row) =>
    [
      "oj-row",
      selectedId === row.id && "is-selected",
      selection?.selectedIds.has(row.id) && "is-checked",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <div className="oj-table-scroll">
      {viewMode === "compact" ? (
        <table className="oj-table oj-table--compact">
          <thead>
            <tr>
              {leadingHeaders}
              <th className="oj-cell--wrap">Счет Дт / Кт</th>
              <th>Аналитики Дт</th>
              <th>Аналитики Кт</th>
              <SortableHeader>Количество</SortableHeader>
              <SortableHeader>Сумма ({amountUnitLabel})</SortableHeader>
              {!isSource && (
                <>
                  <SortableHeader>Источник / Тип данных</SortableHeader>
                  <SortableHeader>Версия бюджета</SortableHeader>
                  <th>Комментарий</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.id}
                className={getRowClassName(row)}
                onClick={() => onRowClick?.(row)}
              >
                {renderLeadingCells(row, index)}
                <td className="oj-cell--wrap">
                  <div className="oj-stack">
                    <div>
                      <span className="oj-stack__prefix">Дт:</span> {row.accountDt}
                    </div>
                    <div className="oj-stack__secondary">
                      <span className="oj-stack__prefix">Кт:</span> {row.accountKt}
                    </div>
                  </div>
                </td>
                <td className="oj-cell--wrap">
                  <AnalyticsCell
                    items={
                      isSource && !row.fromApi
                        ? getSubconto(row, "Dt")
                        : row.analyticsDt
                    }
                  />
                </td>
                <td className="oj-cell--wrap">
                  <AnalyticsCell
                    items={
                      isSource && !row.fromApi
                        ? getSubconto(row, "Kt")
                        : row.analyticsKt
                    }
                  />
                </td>
                <td className="oj-cell--num">
                  {row.quantity == null ? (
                    <span className="oj-empty">—</span>
                  ) : (
                    row.quantity
                  )}
                </td>
                <td className="oj-cell--num oj-cell--amount">
                  {formatAmount(row.amount, unit)}
                </td>
                {!isSource && (
                  <>
                    <td>
                      <div className="oj-stack">
                        <div>{row.source}</div>
                        <div className="oj-stack__secondary">{row.dataType}</div>
                      </div>
                    </td>
                    <td>{renderCell(row.budgetVersion)}</td>
                    <td>{renderCell(row.comment)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="oj-table oj-table--expanded">
          <thead>
            <tr>
              {leadingHeaders}
              {visibleColumns.map((column) => (
                <th key={column.key}>
                  {column.type === "amount"
                    ? `${column.label} (${amountUnitLabel})`
                    : column.label}
                  {column.sortable && (
                    <>
                      {" "}
                      <SortIcon />
                    </>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.id}
                className={getRowClassName(row)}
                onClick={() => onRowClick?.(row)}
              >
                {renderLeadingCells(row, index)}
                {visibleColumns.map((column) => renderExpandedCell(row, column))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default JournalTable;
